<script setup>
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  equipements:          { type: Array,   default: () => [] },
  total_count:          { type: Number,  default: 0 },
  actifs_count:         { type: Number,  default: 0 },
  inactifs_count:       { type: Number,  default: 0 },
  show_type_equipement: { type: Boolean, default: false },
  show_description:     { type: Boolean, default: false },
  can_see_costs:        { type: Boolean, default: false },
  can_update:           { type: Boolean, default: false },
  can_create:           { type: Boolean, default: false },
  can_import:           { type: Boolean, default: false },
})

const openDropdown = ref(null)
function toggleDropdown(id) {
  openDropdown.value = openDropdown.value === id ? null : id
}

const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('code')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.equipements
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(e =>
      e.code.toLowerCase().includes(q) ||
      e.nom.toLowerCase().includes(q) ||
      (e.description && e.description.toLowerCase().includes(q))
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages  = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

const extraCols = computed(() =>
  (props.show_description ? 1 : 0) + (props.can_see_costs ? 1 : 0) + (props.can_update ? 1 : 0)
)

function toggleActif(e) {
  router.delete(e.destroy_url, { preserveScroll: true })
}

function exportCsv() {
  const header = ['Code', 'Nom', 'Description', 'Taux Horaire', 'Actif'].join(',')
  const rows = filtered.value.map(e =>
    [`"${e.code}"`, `"${e.nom}"`, `"${e.description || ''}"`, e.taux_horaire ?? '', e.actif ? 'Oui' : 'Non'].join(',')
  )
  const csv  = '\ufeff' + [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: 'equipements.csv' })
  a.click()
  URL.revokeObjectURL(url)
}

function copyTable() {
  const header = ['Code', 'Nom', 'Description', 'Taux Horaire', 'Actif'].join('\t')
  const rows = filtered.value.map(e =>
    [e.code, e.nom, e.description || '', e.taux_horaire ?? '', e.actif ? 'Oui' : 'Non'].join('\t')
  )
  navigator.clipboard.writeText([header, ...rows].join('\n'))
    .then(() => alert('Tableau copié dans le presse-papiers'))
    .catch(() => alert('Impossible de copier'))
}

function printTable() { window.print() }
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="relative w-80">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher un équipement..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8" @click="openDropdown = null">

      <!-- KPI Cards -->
      <div class="grid grid-cols-3 gap-5 mb-8">
        <div class="bg-surface-container-low border border-outline-variant/10 p-5 rounded-xl flex items-center gap-5">
          <div class="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-2xl text-primary">precision_manufacturing</span>
          </div>
          <div>
            <div class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Total Équipements</div>
            <div class="text-2xl font-black text-on-surface">{{ total_count }}</div>
          </div>
        </div>
        <div class="bg-surface-container-low border border-outline-variant/10 p-5 rounded-xl flex items-center gap-5">
          <div class="w-12 h-12 rounded-lg bg-tertiary/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-2xl text-tertiary">check_circle</span>
          </div>
          <div>
            <div class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Unités Actives</div>
            <div class="text-2xl font-black text-on-surface">{{ actifs_count }}</div>
          </div>
        </div>
        <div class="bg-surface-container-low border border-outline-variant/10 p-5 rounded-xl flex items-center gap-5">
          <div class="w-12 h-12 rounded-lg bg-outline/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-2xl text-outline">engineering</span>
          </div>
          <div>
            <div class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Inactifs</div>
            <div class="text-2xl font-black text-on-surface">{{ inactifs_count }}</div>
          </div>
        </div>
      </div>

      <!-- Page header -->
      <div class="flex justify-between items-end mb-6">
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">Gestion des Équipements</h1>
          <div class="flex items-center gap-4 text-[10px] font-black text-outline uppercase tracking-widest">
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-tertiary"></span>
              {{ actifs_count }} Actifs
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-outline/50"></span>
              {{ inactifs_count }} Inactifs
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
            <select
              v-model="pageSize"
              class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
            >
              <option :value="25">25 Équipements</option>
              <option :value="50">50 Équipements</option>
              <option :value="100">100 Équipements</option>
            </select>
          </div>
          <a
            v-if="can_create"
            href="/equipements/new"
            class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Nouvel Équipement
          </a>
        </div>
      </div>

      <!-- Onglets -->
      <div class="flex items-center gap-1 mb-6">
        <div class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm bg-primary text-on-primary">
          Liste des équipements
        </div>
        <a
          v-if="can_import"
          href="/equipement_imports/new"
          class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all"
        >Importer</a>
        <a
          v-if="can_create"
          href="/equipements/compilation"
          class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all"
        >Compilation</a>
      </div>

      <!-- Table -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
        <table class="w-full text-left border-collapse print-table">
          <thead>
            <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
              <th v-for="col in [
                { key: 'code',         label: 'Code',         sortable: true,  cls: 'w-28' },
                { key: 'nom',          label: 'Nom',          sortable: true,  cls: '' },
                ...(show_description ? [{ key: 'description',  label: 'Description',  sortable: false, cls: '' }] : []),
                ...(can_see_costs    ? [{ key: 'taux_horaire', label: 'Taux Horaire', sortable: true,  cls: 'w-36 text-right' }] : []),
                { key: 'actif',        label: 'Actif',        sortable: true,  cls: 'w-28 text-center' },
                ...(can_update       ? [{ key: null,           label: 'Actions',      sortable: false, cls: 'w-20 text-right' }] : []),
              ]" :key="col.label"
                :class="['px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline', col.cls, col.sortable ? 'cursor-pointer select-none hover:text-on-surface transition-colors' : '']"
                @click="col.sortable && toggleSort(col.key)"
              >
                <span class="inline-flex items-center gap-1">
                  {{ col.label }}
                  <span
                    v-if="col.sortable"
                    class="material-symbols-outlined text-xs"
                    :class="sortKey === col.key ? 'text-primary' : 'text-outline/40'"
                  >{{ sortKey === col.key && sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-outline-variant/10">
            <tr
              v-for="(e, i) in paginated"
              :key="e.id"
              :class="[
                'group hover:bg-surface-container-highest/20 transition-colors',
                i % 2 === 1 ? 'bg-surface-container-low/20' : '',
              ]"
            >
              <!-- Code -->
              <td class="px-6 py-4 align-top" :class="!e.actif ? 'opacity-60' : ''">
                <a :href="e.show_url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ e.code }}</a>
              </td>
              <!-- Nom -->
              <td class="px-6 py-4 align-top" :class="!e.actif ? 'opacity-60' : ''">
                <div class="font-semibold text-on-surface text-sm">{{ e.nom }}</div>
                <div v-if="show_type_equipement && e.type_equipement" class="text-[10px] text-outline uppercase font-bold mt-0.5">{{ e.type_equipement }}</div>
              </td>
              <!-- Description -->
              <td v-if="show_description" class="px-6 py-4 align-top" :class="!e.actif ? 'opacity-60' : ''">
                <span v-if="e.description" class="text-sm text-on-surface-variant italic">{{ e.description }}</span>
                <span v-else class="text-outline">—</span>
              </td>
              <!-- Taux Horaire -->
              <td v-if="can_see_costs" class="px-6 py-4 align-top text-right" :class="!e.actif ? 'opacity-60' : ''">
                <span class="font-mono font-bold text-on-surface text-sm">
                  {{ e.taux_horaire != null ? Number(e.taux_horaire).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' $' : '—' }}
                </span>
              </td>
              <!-- Actif -->
              <td class="px-6 py-4 align-top text-center" :class="!e.actif ? 'opacity-60' : ''">
                <div v-if="e.actif" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-tertiary/10 border border-tertiary/20">
                  <span class="w-1.5 h-1.5 rounded-full bg-tertiary animate-pulse"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-tertiary">Actif</span>
                </div>
                <div v-else class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-surface-container-highest border border-outline-variant/20">
                  <span class="w-1.5 h-1.5 rounded-full bg-outline/50"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-outline">Inactif</span>
                </div>
              </td>
              <!-- Actions -->
              <td v-if="can_update" class="px-6 py-4 align-top text-right">
                <div class="relative inline-block">
                  <button
                    @click.stop="toggleDropdown(e.id)"
                    class="inline-flex p-2 text-outline hover:text-primary hover:bg-primary/10 rounded transition-all"
                  >
                    <span class="material-symbols-outlined text-lg">edit_note</span>
                  </button>
                  <div
                    v-if="openDropdown === e.id"
                    class="absolute right-0 top-full z-50 mt-1 bg-surface-container-high border border-outline-variant/20 rounded-lg shadow-xl min-w-[160px] py-1"
                  >
                    <a :href="e.show_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">visibility</span>
                      Voir
                    </a>
                    <a :href="e.rapport_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">description</span>
                      Rapports
                    </a>
                    <a :href="e.edit_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">edit_note</span>
                      Modifier
                    </a>
                    <button
                      class="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-surface-container-highest transition-colors"
                      :class="e.actif ? 'text-outline hover:text-error' : 'text-outline hover:text-tertiary'"
                      @click.stop="toggleActif(e); openDropdown = null"
                    >
                      <span class="material-symbols-outlined text-base">power_settings_new</span>
                      {{ e.actif ? 'Désactiver' : 'Activer' }}
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            <!-- Empty state -->
            <tr v-if="paginated.length === 0">
              <td :colspan="3 + extraCols" class="px-6 py-16 text-center text-outline text-sm">
                <span class="material-symbols-outlined text-4xl block mb-3 text-outline/40">precision_manufacturing</span>
                Aucun équipement trouvé{{ search ? ` pour "${search}"` : '' }}.
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination footer -->
        <div class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, filtered.length) }}
            sur {{ filtered.length }} équipement{{ filtered.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:text-outline/40 transition-colors"
              @click="currentPage--"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button
                v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary'
                ]"
                @click="currentPage = p"
              >{{ p }}</button>
              <span
                v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm"
              >…</span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:text-outline/40 transition-colors"
              @click="currentPage++"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Export buttons -->
      <div class="flex items-center gap-3 mt-5 no-print">
        <button
          class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="exportCsv"
        >
          <span class="material-symbols-outlined text-base">download</span>
          Excel / CSV
        </button>
        <button
          class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="printTable"
        >
          <span class="material-symbols-outlined text-base">print</span>
          Imprimer
        </button>
        <button
          class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="copyTable"
        >
          <span class="material-symbols-outlined text-base">content_copy</span>
          Copier
        </button>
      </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>

<style>
@media print {
  .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
  .print-table th, .print-table td { color: black !important; border: 1px solid #ccc !important; }
}
</style>
